// Pages that belong to a module but have no tile of their own, so "Go to"
// (built from modules only) could not find them. Each rides on its parent
// module: it is offered exactly when that module is reachable (permission,
// entitlement and tenant-kind gate all come from navModulesFor), so an entry
// can never lead somewhere the route gate refuses. No new module key: the
// four-registry rule does not apply (F19 UF-17).
import { rankByLabel, type ModuleDef } from '@/lib/modules'

export interface ModulelessPage {
  id: string
  /** Parent module key: shown only while that module is in the reachable list. */
  parentKey: string
  label: string
  description: string
  path: string
  /** Extra words the Go to filter matches on. */
  keywords: string
}

export const MODULELESS_PAGES: ModulelessPage[] = [
  {
    id: 'printing-setup', parentKey: 'pallets', label: 'Printing setup',
    description: 'Printer, print bridge and pallet label template for this PC',
    path: '/printing-setup', keywords: 'printer label station zebra argox bridge template print',
  },
  {
    id: 'label-designer', parentKey: 'pallets', label: 'Label designer',
    description: 'Design and test-print your own labels',
    path: '/label-designer', keywords: 'label design sticker layout roll print',
  },
]

/** The tile-less pages reachable given the modules the user can already reach. */
export function modulelessPagesFor(reachable: ModuleDef[]): ModulelessPage[] {
  const keys = new Set(reachable.map(m => m.key))
  return MODULELESS_PAGES.filter(p => keys.has(p.parentKey))
}

/** One row of the palette's Go to group: a module tile or a tile-less page. */
export type NavEntry = { module: ModuleDef; page?: undefined } | { page: ModulelessPage; module?: undefined }

/**
 * Modules and tile-less pages ranked TOGETHER by the one label rule
 * (rankByLabel), so "label designer" + Enter opens Label designer, not a tile
 * whose description mentions labels. Ties keep modules first, then page order.
 */
export function rankNavEntries(modules: ModuleDef[], pages: ModulelessPage[], query: string): NavEntry[] {
  const all: NavEntry[] = [...modules.map(module => ({ module })), ...pages.map(page => ({ page }))]
  return rankByLabel(all, query, e => e.module
    ? { label: e.module.label, text: `${e.module.label} ${e.module.description} ${e.module.category}` }
    : { label: e.page.label, text: `${e.page.label} ${e.page.description} ${e.page.keywords}` })
}

/** Same rule as the palette's module filter: every token in label/description/keywords. */
export function filterModulelessPages(pages: ModulelessPage[], q: string): ModulelessPage[] {
  if (!q) return pages
  const tokens = q.split(/\s+/)
  return pages.filter(p => {
    const hay = `${p.label} ${p.description} ${p.keywords}`.toLowerCase()
    return tokens.every(t => hay.includes(t))
  })
}
